import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  ColDefBuilder,
  DataTable,
  Stack,
  Tag,
  Text,
  makeDefaultSortableColumnDef,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useMedplumPatientAccess } from '@eluve/medplum-client';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';

import { carePlanPlanTitle } from './carePlanFhir';
import { usePatientCarePlans } from './usePatientCarePlans';

type CarePlanRow = {
  id: string;
  title: string;
  status: string;
  started: string;
};

export const PatientCarePlans: React.FC = () => {
  const { t } = useTranslation();
  const { patientId } = useParams() as { patientId: string };
  const isCarePlansEnabled = useIsPostHogFeatureFlagEnabled('CARE_PLANS');

  const columns = useMemo(
    () =>
      new ColDefBuilder<CarePlanRow>()
        .colDef({
          ...makeDefaultSortableColumnDef(
            'title',
            t('patients.carePlans.plan'),
          ),
          cell: ({ row }) => (
            <Link
              to={row.original.id}
              className="text-sm font-medium text-text-accent-secondary hover:underline"
            >
              {row.original.title}
            </Link>
          ),
        })
        .colDef({
          ...makeDefaultSortableColumnDef(
            'status',
            t('patients.carePlans.status'),
          ),
          cell: ({ row }) => {
            const { status } = row.original;
            return (
              <Tag
                label={status}
                color={status === 'active' ? 'lightgreen' : 'gray'}
              />
            );
          },
        })
        .dateSortable('started', t('patients.carePlans.started'))
        .build(),
    [t],
  );

  const {
    isLoading: isAccessLoading,
    hasAccess,
    fhirPatientId,
  } = useMedplumPatientAccess(patientId);
  const { carePlans, isLoading, isError } = usePatientCarePlans(fhirPatientId);

  if (!isCarePlansEnabled) return null;

  if (isAccessLoading) {
    return <Text color="tertiary">{t('patients.common.checkingAccess')}</Text>;
  }

  if (!hasAccess) {
    return (
      <Text color="tertiary">{t('patients.carePlans.noFhirForList')}</Text>
    );
  }

  if (isLoading) {
    return <Text color="tertiary">{t('patients.carePlans.loading')}</Text>;
  }

  if (isError) {
    return <Text color="tertiary">{t('patients.carePlans.loadError')}</Text>;
  }

  if (carePlans.length === 0) {
    return <Text color="tertiary">{t('patients.carePlans.empty')}</Text>;
  }

  const rows = carePlans.map<CarePlanRow>((cp) => ({
    id: cp.id,
    title: carePlanPlanTitle(cp),
    status: cp.status ?? 'unknown',
    started: cp.period?.start ?? '',
  }));

  return (
    <Stack gap={3} className="pt-2">
      <DataTable
        columns={columns}
        data={rows}
        initialSortingState={[{ id: 'started', desc: true }]}
      />
    </Stack>
  );
};
